/* eslint-disable import/prefer-default-export, max-len */
// Source of truth for the in-app per-step explainer copy on the
// Page Optimization wizard. Edit this file to update what users see
// at the top of each tab. See:
// docs/superpowers/specs/2026-05-07-page-optimization-step-docs-design.md

export const stepDocs = {
  overview: {
    label: 'Overview',
    purpose: "A quick read on this page — its slug, page type, content extraction status, and which integrations (GSC, GA, WordPress) are wired up. Nothing happens here automatically; it's just orientation before you start working through the wizard.",
    inputs: [
      'The page record itself. If it was imported from the sitemap or pasted in manually, basic metadata is already populated.',
      'Optionally, content extraction (the "Extract Content" button at the top) — needed before downstream steps that reason about your page\'s headings or word count.'
    ],
    process: [
      'Read-only display of page type (service page, location page, etc.), category, status, GSC/GA enable flags, and whether content has been extracted.',
      'The Re-extract button at the top of the panel re-fetches the live HTML and re-parses headings/meta/body — useful if the page has changed since the last extraction.'
    ],
    outputs: [
      'Nothing on its own. This tab is informational. Re-extraction does update the underlying `extracted_content`, `headings`, and `meta_*` fields used by Counting and Topics later.'
    ],
    gotchas: [
      'If Counting or Topics show stale numbers, come back here and Re-extract first.',
      'This tab is never locked — every other tab is.'
    ]
  },

  analytics: {
    label: 'Analytics',
    purpose: 'See how this page actually performs in Google before you decide whether to optimize it. Clicks, impressions, CTR, position from Search Console; sessions and conversions from GA. The signal that drives the priority score on the page list.',
    inputs: [
      'GSC enabled for the website AND for this specific page URL.',
      'GA4 enabled for the website (optional but useful).',
      'A daily sync to have fired at least once — analytics data lags 2–3 days.'
    ],
    process: [
      'Renders the page-level GSC + GA stats over the selected window.',
      'Surfaces top queries this page already ranks for (which can inform Keywords selection later).',
      'Charts trends so you can see if performance is rising, flat, or declining.'
    ],
    outputs: [
      'No DB writes. Pure visualization.',
      'Indirectly: the priority score on the page list is computed from the same underlying data.'
    ],
    gotchas: [
      'If you see "No data" and you expect data, check that the page URL is enabled in GSC (Sites → Settings) and that the daily sync has run.',
      'GSC reports up to 90 days of history by default. Older data may be unavailable.'
    ]
  },

  keywords: {
    label: 'Keywords',
    purpose: 'Pick the single primary keyword this page should target. The keyword you choose here is the input for everything downstream — SERP analysis, competitor selection, content strategy, counting, headings, meta. Choose well.',
    inputs: [
      'Extracted page content (run Re-extract on the Overview tab first if needed).',
      'Business profile (so the AI can suggest locally-relevant variants).'
    ],
    process: [
      '"Run Keyword Research" generates ~20 candidate keywords using an LLM that reads your page content and your business context.',
      'Each candidate is scored for opportunity using search volume, CPC, and competition (enrichment from a third-party keyword data provider).',
      'Candidates are ranked; you review and click to select one. Your selection is stored on the page record.'
    ],
    outputs: [
      'A `selected_keyword_candidate_id` on the website page. Every downstream tab reads this.',
      'A `keyword_research` record with all candidates (so you can come back and pick a different one without re-running the AI).'
    ],
    gotchas: [
      'Running keyword research again creates a fresh batch of candidates but does NOT discard your downstream work — the selected candidate stays the same until you change it.',
      "Changing the SELECTED keyword (picking a different candidate) will surface a banner on the SERP tab prompting you to re-analyze. Doing so discards Competitors → Strategy → Counting → Topics → Structure for this page. Don't change the keyword casually after step 5."
    ]
  },

  serp: {
    label: 'SERP Analysis',
    purpose: "Figure out what Google is actually rewarding for your keyword — informational vs commercial vs transactional intent, the dominant content format (guide, listicle, service page, comparison, etc.), and which SERP features (AI Overview, People Also Ask, Local Pack, featured snippet, video, news) are showing up. This shapes everything else.",
    inputs: [
      'A selected primary keyword (from the Keywords tab).',
      'A selected SERP location from the page target, linked page locations, business locations, or United States fallback.'
    ],
    process: [
      'Hits SerpAPI for the selected location to fetch the top 10 organic results plus all SERP feature blocks.',
      'Detects which features are present (AI Overview, PAA, knowledge graph, local pack, images, videos, etc.) and writes specific findings and optimization notes per feature.',
      'An LLM classifies search intent (informational/commercial/transactional/mixed) and the dominant content format based on the top 10 titles, snippets, and your page type.',
      "Surfaces a format-mismatch warning if your page's apparent format (e.g., service-page) differs from what's ranking (e.g., a guide)."
    ],
    outputs: [
      'A `serp_analysis` record with selected location, pull timestamp, top 10 organic evidence, raw SERP data, intent, content format, feature findings, PAA questions, AI Overview text + sources.',
      'Auto-confirms on completion, which unlocks the Competitors tab.'
    ],
    gotchas: [
      'If the selected keyword changes after this step, a banner here will prompt you to re-analyze. Re-analyzing discards everything downstream (Competitors → Strategy → Counting → Topics → Structure).',
      'An always-available Re-analyze link on the completed view lets you re-roll if the result feels wrong; this also discards downstream work.',
      'For multi-location clients, confirm the selected SERP location before running or re-running analysis.'
    ]
  },

  competitors: {
    label: 'Competitors',
    purpose: "Build a model of what's working for the top organic results — extract their content, score quality, set benchmarks. Everything you optimize from here on is \"match or beat the consensus of the verified winners.\"",
    inputs: [
      'A confirmed SERP analysis (organic results to choose from).',
      'Working URLs (some competitor sites block scrapers — see Gotchas).'
    ],
    process: [
      'You select as many competitor URLs as you like from up to 20 SERP results (top 7 pre-selected), add manual competitor URLs, or let auto-select take the top 10.',
      'Background workers fetch the HTML for each, extract content (body text, headings, meta title, meta description, word count), and verify the keyword appears.',
      'An LLM grades each page on heading quality, title quality, and meta quality (A/B/C/D/F). The overall grade rolls up into a "verified," "corrected," or "excluded" tier.',
      'Quality benchmarks (avg word count, heading rate, etc.) are computed across the verified set — these become the targets for your page.',
      'You approve the quality profile; that unlocks Strategy.'
    ],
    outputs: [
      'Verified `competitor_pages` with extracted content, headings, meta, and quality grades. Used by Strategy, Counting, Topics, and Structure.',
      '`competitor_benchmarks` (e.g., target word count) used to score over-optimization later.',
      'A confirmed quality profile unlocks the Keyword Strategy tab.'
    ],
    gotchas: [
      "Extraction can fail on JavaScript-heavy sites or sites that aggressively block scrapers. Replace those URLs with the next-best result and re-run.",
      "A competitor with a non-matching keyword (the keyword doesn't appear on their page) gets flagged for correction or exclusion. Don't include excluded sites in your benchmark set."
    ]
  },

  strategy: {
    label: 'Keyword Strategy',
    purpose: 'Build the target keyword list — the specific keywords your page needs to mention (and roughly how often) based on what the verified competitor pages actually use. This is the source of truth for the Counting tab and the keyword-target locks at the bottom of Structure.',
    inputs: [
      'An approved competitor quality profile (Competitors tab).',
      'Extracted competitor content (used to mine keyword usage).'
    ],
    process: [
      'An LLM mines the verified competitor pages for keywords, tiers them (core / high_value / supporting / long_tail) by frequency and relevance, and recommends an inclusion bucket (include / evaluate / exclude) for each.',
      'You review the table, edit/include/exclude as needed, and approve.',
      'The approved keyword list becomes the master target list — used by Counting (to count occurrences) and Structure (to lock target counts).'
    ],
    outputs: [
      'A `keyword_strategy` with `keyword_strategy_items` (one per target keyword) carrying tier, bucket, and the per-competitor counts that drove the tier assignment.',
      'Approving the strategy unlocks the Counting tab.'
    ],
    gotchas: [
      "Re-running the strategy generation discards Counting + Structure work for this page. If you've made edits to the table, save them by approving before re-running.",
      '"Evaluate" bucket items are not auto-included — you decide. Don\'t leave the page with a pile of unreviewed evaluate items.'
    ]
  },

  counting: {
    label: 'Counting',
    purpose: 'Count how often your current page mentions each target keyword vs how often the competitors mention it, with the same counting rules applied to both. Surfaces the gap you need to close.',
    inputs: [
      'An approved keyword strategy (target keyword list).',
      'Extracted client and competitor content.'
    ],
    process: [
      'A counting engine runs the same regex-based occurrence count over your client content and each competitor, with track_placements enabled (first 100 words, in_bold, in_alt_text, in_anchor_text, section_openers, in_headings).',
      'Builds a master table per keyword: `client count / target count / per-competitor counts / cleaned average / target count`. Cleaned average drops outliers.',
      'Computes deltas: heading deltas (H1/H2/H3 status — critical/should add), body deltas (under/on-target/over), placement gaps (placement types where client lags competitors), TF-IDF notes.',
      'Flags over-optimization (your count >2x the competitor average) for keywords you should consider trimming.',
      'An LLM generates a delta summary that prioritizes the work.'
    ],
    outputs: [
      'A `keyword_occurrence` record with master table, deltas, over-optimization flags, and the AI-prioritized summary. Used by the Structure tab to inform meta/heading writing and to lock target counts.',
      'Completion unlocks the Topics tab.'
    ],
    gotchas: [
      'If your page changed and the master table looks wrong, go back to Overview and Re-extract before re-running counting.',
      'Over-optimization flags are advisory — review each one with the option to exclude/cap/keep before locking targets in Structure.',
      'Placement gaps now show "Missing From" (which placement types you lack) and "Competitor Avg" (the avg competitor count for those types). If you see blanks, you\'re on a stale record — re-run counting.'
    ]
  },

  topics: {
    label: 'Topics',
    purpose: 'Identify the content TOPICS your page is missing relative to the SERP winners — the semantic gaps, not just keyword gaps. Two pages can share keywords and still cover entirely different ground; this catches that.',
    inputs: [
      'Verified competitor content (Competitors tab).',
      'Extracted client content.'
    ],
    process: [
      'An LLM extracts subject-predicate-object triples from each verified competitor page (e.g., "carpet beetles | damage | wool fabrics").',
      'Triples are clustered into topics; competitor coverage is summed; client coverage is checked.',
      'Topics are tagged `must_add` (covered by ≥3 competitors, missing on your page), `could_add` (covered by some, optional), or `covered` (your page already has it).',
      'Each topic gets an estimated word count budget — how much copy you\'d need to add to cover it adequately.',
      'You select which topics to commit to (must_add are pre-checked); approval unlocks Structure.'
    ],
    outputs: [
      "A `semantic_analysis` record with triple_scores and topic_recommendations + the user's selections. Used by Structure to plan headings (each selected topic typically becomes an H2) and to constrain Content rewriting.",
      'Approval unlocks the Structure tab.'
    ],
    gotchas: [
      'The word count budget is an LLM estimate. Treat it as a planning aid, not a contract.',
      "Don't add every \"could_add\" topic — pages that cover everything cover nothing well. Pick the ones that align with your business."
    ]
  },

  structure: {
    label: 'Structure',
    purpose: "Lock in the on-page SEO foundations — title tag, meta description, H1, full heading structure (H2/H3/H4), and target keyword counts — before you hand any of it to the content rewriter. This step's locks are the contract the Content tab will honor verbatim.",
    inputs: [
      'A locked-in keyword (Keywords tab).',
      'An approved keyword strategy (Strategy tab).',
      'An approved topic plan (Topics tab).',
      'Counting results (provides target counts and over-optimization context).'
    ],
    process: [
      'Five sub-steps in order: Title Tag → Meta Description → H1 → Heading Structure → Keyword Verification → Target Lock.',
      'For each of Title/Meta/H1/Headings, an LLM generates options (3 titles, 2 metas, 3 H1s, full heading tree). You pick one or write a custom value. Each step has a Regenerate button + optional guidance text input ("shorter", "include free quote", "add an FAQ section") if the suggestions miss.',
      'Title and Meta validate that the primary keyword is present (and meta detects a CTA). Length is shown as a soft warning, not enforced.',
      'Verify Heading Keywords runs after the heading tree is locked: it scans your locked headings to check which target keywords are covered. You see which keywords are still missing.',
      'Lock Targets fixes the per-keyword target counts that the Content tab must hit.'
    ],
    outputs: [
      'A `heading_meta_lock` with `locked_title_tag`, `locked_meta_description`, `locked_h1`, `locked_heading_structure`, `locked_targets`, and `targets_validated`. Everything Content needs.',
      'Lock approval unlocks the Content tab.'
    ],
    gotchas: [
      'Title and meta length are shown as red/amber/gray badges (60 / 155 char targets) but are NOT enforced — you can lock something over-length if you want. The badges are just a hint.',
      'Title MUST contain the primary keyword. Meta MUST contain the primary keyword AND a call-to-action ("Learn more", "Get started", "Call today", etc.). These are enforced.',
      'Re-generating heading structure replaces your inline edits — there\'s a heads-up amber banner on that step.',
      'Each step has an Unlock button if you need to go back and re-do it.'
    ]
  },

  content: {
    label: 'Content',
    purpose: "Produce optimized content that honors everything you've locked above — the title, meta, H1, heading structure, target keyword counts, and topic commitments. The output is a deliverable ready to publish to WordPress.",
    inputs: [
      'A fully completed Structure tab (all locks set, targets validated).',
      'All upstream context (intent, format, competitor benchmarks, topic plan, counting deltas).'
    ],
    process: [
      'An LLM rewrites the page content section by section, hitting target keyword counts, covering the committed topics, fitting the locked heading structure.',
      'A density audit runs across the rewritten content to verify no over-optimization slipped in.',
      'A FAQ generator builds answers to the PAA questions captured in SERP analysis.',
      'A schema generator emits structured-data JSON-LD for the page (article / service / FAQ depending on type).',
      'A final audit scores the deliverable (heading quality, keyword coverage, word count vs benchmark) and surfaces any remaining gaps.',
      'You accept the deliverable. The page transitions to `deliverable_accepted` and the "Publish to WP" button appears in the page header.'
    ],
    outputs: [
      'An optimized content draft, density audit, FAQ block, schema JSON-LD, and a final audit score.',
      'A `deliverable_accepted` status that enables Publish to WP.'
    ],
    gotchas: [
      'Review the deliverable carefully before publishing. The AI honors the locks but can still pick odd phrasings.',
      '"Publish to WP" requires an active WordPress connection on the website. If the button is greyed out, check the website\'s WP integration settings.',
      'Republishing overwrites the live page. There is no automatic backup.'
    ]
  }
}
